import { isDiagnostic, isUnfinished } from "@helpers/exams";
import {
	type SkillIdentity,
	examCoversSkill,
	getAnsweredCount,
} from "@memorang/helpers/language-learning";
import type { MixedItemTag, PracticeTestExam } from "@memorang/types";
import type { StatTileCta } from "@memorang/ui/components/StatTile";
import type { TFunction } from "i18next";
import { selectRecommendedTestsForSkill } from "./recommended-tests";

export type SkillCtaKind = "diagnostic" | "practice";

export const getSkillCtaKind = ({
	skill,
	startableTests,
	hasPracticeRecommendation,
}: {
	skill: SkillIdentity;
	startableTests: PracticeTestExam[] | undefined;
	hasPracticeRecommendation: boolean;
}): SkillCtaKind | undefined => {
	const [topTest] = selectRecommendedTestsForSkill({
		exams: startableTests,
		skill,
	});
	if (topTest) {
		return topTest.kind === "diagnostic" ? "diagnostic" : "practice";
	}
	return hasPracticeRecommendation ? "practice" : undefined;
};

const CTA_LABEL_KEY: Record<SkillCtaKind, string> = {
	diagnostic: "dashboard.stats.skillCta.diagnostic",
	practice: "dashboard.stats.skillCta.practice",
};

export const buildSkillCta = (
	kind: SkillCtaKind | undefined,
	t: TFunction,
): StatTileCta | undefined =>
	kind
		? { icon: "play-circle-outline", label: t(CTA_LABEL_KEY[kind]) }
		: undefined;

/**
 * Whether there's enough evidence to show this skill's estimated level
 * instead of a recommended-action CTA: the learner has completed the
 * skill's diagnostic, or has answered any items for the skill at all.
 */
export const hasSkillLevelEvidence = ({
	skill,
	exams,
}: {
	skill: MixedItemTag;
	exams: PracticeTestExam[] | undefined;
}): boolean =>
	getAnsweredCount(skill) > 0 ||
	(exams ?? []).some(
		(exam) =>
			isDiagnostic(exam) && !isUnfinished(exam) && examCoversSkill(exam, skill),
	);
